/**
 * A caixa de cartas no painel: o `letterbox_view.dart` com o `note_card.dart`
 * por dentro.
 *
 * É a fatia de `dashboard_screen.dart:396`, e é a única sem `so:` na lista de
 * lá — a caixa de cartas não desliga.
 *
 * Uma carta é um bilhete com dia marcado. Até lá ela aparece lacrada, com a
 * tranca e o quanto falta escrito embaixo; depois abre, e enquanto ninguém a
 * leu ela fica com a borda de ouro e o coração aceso.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **Sem o botão de zerar exemplos.** O `_seedSampleNotes` de lá é ferramenta
 *   de quem estava desenvolvendo: enche a caixa de cartas de mentira. Não é
 *   coisa que se dá a quem mora na casa.
 * - **Sem emoji e sem gaveta.** O recado da caixa vazia perde o `'🌸'` (regra
 *   da casa), e o toque de cada carta lá abre uma folha que sobe. No site as
 *   cartas têm tela própria com essa folha dentro, então a seção inteira leva
 *   a `/letterbox` e as cartas saem sem clique — um `<button>` dentro de um
 *   `<a>` é HTML inválido.
 */

import { useMemo } from 'react';
import {
  COLECOES,
  aberta,
  abreEmCurto,
  cartas,
  traduzir,
  usarColecao,
  type Carta,
} from '@nosso-sempre/shared';
import { Icone } from './Icone';
import { Secao, SetaDaSecao } from './Secao';
import './previas.css';

export function PreviaDasCartas() {
  const documentos = usarColecao(COLECOES.notas);
  const caixa = useMemo(() => cartas(documentos), [documentos]);
  const agora = new Date();

  return (
    <Secao
      icone="carta"
      cor="var(--ouro)"
      titulo="Letterbox"
      para="/letterbox"
      ponta={<SetaDaSecao />}
    >
      {caixa.length === 0 ? (
        <p className="previa-convite">
          <Icone nome="carta" tamanho={18} />
          {traduzir('Nenhuma carta ainda. Continue voltando aqui!')}
        </p>
      ) : (
        <ul className="previa-estante">
          {caixa.map((c) => (
            <li key={c.id}>
              <CartaDaCaixa carta={c} agora={agora} />
            </li>
          ))}
        </ul>
      )}
    </Secao>
  );
}

/** O `NoteCard`: 150 por 180, o desenho grande no meio e o título embaixo. */
function CartaDaCaixa({ carta, agora }: { carta: Carta; agora: Date }) {
  const abriu = aberta(carta, agora);
  const nova = abriu && !carta.lida;
  const classe = `previa-carta${nova ? ' nova' : ''}${abriu && carta.lida ? ' lida' : ''}`;

  return (
    <span className={classe}>
      <span className="previa-carta-selo" aria-hidden="true">
        <Icone nome={abriu ? (carta.lida ? 'carta-aberta' : 'coracao') : 'tranca'} tamanho={36} />
      </span>
      <span className="previa-carta-nome">{carta.titulo}</span>
      {abriu ? null : (
        <span className="previa-carta-falta">{abreEmCurto(carta.abreEm, agora)}</span>
      )}
    </span>
  );
}
